import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ObjetivosPage from './pages/ObjetivosPage';
import ObjetivoDetailPage from './pages/ObjetivoDetailPage';
import NewObjetivoPage from './pages/NewObjetivoPage';
import KeyResultDetailPage from './pages/KeyResultDetailPage';
import NewKeyResultPage from './pages/NewKeyResultPage';
import WorkspacesPage from './pages/WorkspacesPage';
import WorkspaceUsersPage from './pages/WorkspaceUsersPage';
import UsersPage from './pages/UsersPage';
import './index.css';

// Protected Route Component with detailed logging
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  console.log('🔒 PrivateRoute check:', { user: !!user, loading });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }
  
  if (!user) {
    console.log('❌ No user, redirecting to login');
    return <Navigate to="/" replace />;
  }
  
  console.log('✅ User authenticated, rendering protected content');
  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  console.log('🌐 PublicRoute check:', { user: !!user, loading });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }
  
  if (user) {
    console.log('✅ User already logged in, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }
  
  console.log('🌐 Rendering public route');
  return children;
};

function App() {
  console.log('🚀 App component rendering');
  
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          
          {/* Protected Routes - All wrapped in Layout */}
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/objetivos" 
            element={
              <PrivateRoute>
                <Layout>
                  <ObjetivosPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/objetivos/nuevo" 
            element={
              <PrivateRoute>
                <Layout>
                  <NewObjetivoPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/objetivos/:id" 
            element={
              <PrivateRoute>
                <Layout>
                  <ObjetivoDetailPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/objetivos/:objectiveId/kr/nuevo" 
            element={
              <PrivateRoute>
                <Layout>
                  <NewKeyResultPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/objetivos/:objectiveId/kr/:id" 
            element={
              <PrivateRoute>
                <Layout>
                  <KeyResultDetailPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/workspaces" 
            element={
              <PrivateRoute>
                <Layout>
                  <WorkspacesPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/workspaces/:workspaceId/usuarios" 
            element={
              <PrivateRoute>
                <Layout>
                  <WorkspaceUsersPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/usuarios" 
            element={
              <PrivateRoute>
                <Layout>
                  <UsersPage />
                </Layout>
              </PrivateRoute>
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
